import { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CONSTANTS from "../../../utils/constants";
import { UUIDContext, PBLResponseContext } from "../../../UUIDContexted";
import { competeInstallmentOptionPayment } from "../../../api/apiService";
import { ArrowDownCircle } from 'react-bootstrap-icons';
import { v4 as uuidv4 } from 'uuid';

import "./installmentOptions.css";
import { Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FormFieldError } from "../../../components/Installment";
import { currencyToSymbol, formatAmount } from "../../../utils/utils";
import { IInstallmentOption, IFormData } from "../../../interfaces/InstallmentOptionsProps";
import { PaymentProcessingLoader } from "../../../components/PaymentProcessingLoader.tsx";
import PaylaDcs from "../../../components/PaylaDsc";
import { usePaymentForm } from "../../../context/PaymentFormContext";
import Installment from "../../../components/Installment";

const InstallmentOptions: React.FC<{ updatedFormData: IFormData, InstallmentOptionsData: any }> = ({ updatedFormData, InstallmentOptionsData }) => {
  const PBLData = useContext(UUIDContext);
  const { setPblResponseData } = useContext(PBLResponseContext);
  const navigate = useNavigate();
  const intl = useIntl();

  const [selectedMethod, setSelectedMethod] = useState<null | number>(null);
  const [selectedInstallmentDetails, setSelectedInstallmentDetails] = useState<IInstallmentOption | undefined>();
  const [installmentOptionsList, setInstallmentOptionsList] = useState<IInstallmentOption[]>();
  const [agreedCheckbox, setAgreedCheckbox] = useState(false);
  const [notAgreedError, setNotAgreedError] = useState('');
  const [showPaymentProcessing, setShowPaymentProcessing] = useState(false);
  const [snippetToken, setSnippetToken] = useState("");

  const {  PaymentFormData } = usePaymentForm();

  const isOverviewPage = PaymentFormData.isOverviewPage;

  useEffect(() => {
    if (PBLData) {
      //generate snippetToken only once
      const partnerMerchantId = PBLData ? PBLData.bnplId : '';
      const snippetToken = `${CONSTANTS.PAYLA_PARTNER_ID}_${partnerMerchantId}_${uuidv4()}`;
      setSnippetToken(snippetToken);

      setInstallmentOptionsList(InstallmentOptionsData?.createPaymentResponse?.payment?.paymentOutput?.financingPaymentMethodSpecificOutput.paymentProduct3391SpecificOutput.installmentOptions);

    }
  }, [PBLData]);

  const handleMethodSelect = (optionId: number) => {
    setSelectedMethod(optionId);
    setSelectedInstallmentDetails(installmentOptionsList?.find((option: { numberOfPayments: number; }) => option.numberOfPayments === optionId));
  };

  const completePayment = async () => {
    
    if (!agreedCheckbox) {
      setNotAgreedError(CONSTANTS.VALIDATION_ERRORS.TERMS_AND_CONDITIONS.NOT_AGREED);
      return;
    }
    try {
      setShowPaymentProcessing(true);
      setPblResponseData(null);
      const response = await competeInstallmentOptionPayment({ ...updatedFormData, installmentOptionId: selectedInstallmentDetails?.installmentOptionId });
      const paymentStatus = response.status;

      if (paymentStatus === "PENDING_CAPTURE" || paymentStatus === "CREATED" || paymentStatus === "CAPTURED") {
        setPblResponseData({ payment: response, linkId: PBLData?.linkId })
        navigate(`/${PBLData?.linkId}/success/`);
      }
      else {
        setPblResponseData({ ...response, linkId: PBLData?.linkId })
        setShowPaymentProcessing(false);
        // navigate(`/${PBLData?.linkId}/error/`);
      }
    } catch (error) {
      setPblResponseData({ error: error, linkId: PBLData?.linkId });
      setShowPaymentProcessing(false);
      // navigate(`/${PBLData?.linkId}/error/`);
    }
  }

  return (
    <div className="installment-options">
       {isOverviewPage &&  <Installment /> }
      {installmentOptionsList &&
        <div className="payment-selector" data-testid="payment-selector-container">
          <div className="headline">
            <div className="frame-74371">
              <div className="labelPaymentHeading">
                <FormattedMessage
                  id={CONSTANTS.INSTALLMENT_OPTIONS.HEADING}
                  defaultMessage="Secured Installment"
                />
              </div>
            </div>
            <div className="volte-medium">
              <FormattedMessage
                id={CONSTANTS.INSTALLMENT_OPTIONS.SUB_HEADING}
                defaultMessage="Please select one of the rates"
              />
            </div>
          </div>

          <div className="payment-methods2" data-testid="payment-options-container">
            {installmentOptionsList.map((option: IInstallmentOption) => (
              <div
                key={option.numberOfPayments}
                data-testid={`payment-option-${option.numberOfPayments}`}
                className={`payment-option ${selectedMethod === option.numberOfPayments ? "active" : ""}`}
                onClick={() => handleMethodSelect(option.numberOfPayments)}
                role="radio"
                aria-checked={selectedMethod === option.numberOfPayments}
              >
                <div className="content">
                  <div className="custom-radio">
                    <div className="bg">
                      <input
                        type="radio"
                        key={option.numberOfPayments}
                        name="payment-option"
                        checked={selectedMethod === option.numberOfPayments}
                        className="custom-radio"
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="text">
                    <div
                      className="volte-medium"
                      data-testid={`payment-label-${option.numberOfPayments}`}
                    >
                      <span className="volte-medium">
                        {option.numberOfPayments}&nbsp;<FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.RATES_PER} defaultMessage="rates per" />&nbsp;
                        <span className="amount">{formatAmount(option.monthlyAmount.amount)} {currencyToSymbol(option.monthlyAmount.currencyCode)}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedMethod && <div>
            <div><b><FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.DETAILS.RATE_DETAILS} defaultMessage="Rate details" /></b></div>
            {selectedInstallmentDetails ? <div className="installment-option-details pb-4">
              <div className="mt-1" data-testid="number-of-payments" >
                <b className="volte-semibold">{selectedInstallmentDetails.numberOfPayments}</b>&nbsp;
                <span className="volte-medium"><FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.DETAILS.NUMBER_OF_PAYMENTS} defaultMessage="- Number of payments" /></span>
              </div>
              <div className="mt-1" data-testid="monthly-amount-value" >
                <b className="volte-semibold">{formatAmount(selectedInstallmentDetails.monthlyAmount.amount)} {currencyToSymbol(selectedInstallmentDetails.monthlyAmount.currencyCode)}</b>&nbsp;
                <span className="volte-medium"><FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.DETAILS.MONTHLY_AMOUNT_VALUE} defaultMessage="- Monthly amount value" /></span>
              </div>
              <div className="mt-1">
                <b className="volte-semibold">{formatAmount(selectedInstallmentDetails.totalAmount.amount)} {currencyToSymbol(selectedInstallmentDetails.totalAmount.currencyCode)}</b>&nbsp;
                <span className="volte-medium"><FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.DETAILS.TOTAL_AMOUNT} defaultMessage="- Total amount" /></span>
              </div>
              <div className="mt-1" data-testid="debit-interest">
                <b className="volte-semibold">{formatAmount(selectedInstallmentDetails.nominalInterestRate)} %</b>&nbsp;
                <span className="volte-medium"><FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.DETAILS.DEBIT_INTEREST_PA_FIXED} defaultMessage="- Debit interest p.a. (fixed)" /></span>
              </div>
              <div className="mt-1" data-testid="effective-interest-rate">
                <b className="volte-semibold">{formatAmount(selectedInstallmentDetails.effectiveInterestRate)} %</b>&nbsp;
                <span className="volte-medium"><FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.DETAILS.EFFECTIVE_INTEREST_RATE} defaultMessage="- Effective interest rate" /></span>
              </div>
              <div className="installment-info mt-2">
                <b>
                  <a className="volte-semibold" href={selectedInstallmentDetails.creditInformation.href} target="_blank" rel="noopener noreferrer" data-testid="pm-link-installment info">
                    <FormattedMessage id={CONSTANTS.INSTALLMENT_OPTIONS.DETAILS.DOWNLOAD_INSTALLMENT_INFORMATION} defaultMessage="Download installment information" />
                  </a>
                </b>
                <ArrowDownCircle />
              </div>
            </div> : <div><b><FormattedMessage id={CONSTANTS.NO_DATA_AVAILABLE} defaultMessage="No Data Available" /></b></div>}

            <Row className="d-flex justify-content-center align-items-end form-section mt-4">
              <h5 data-testid="pm-label-terms-payment" className="section-title">
                {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_SEC_TITLE, defaultMessage: "Terms of Payment" })}
              </h5>
              <div className="terms-checkbox mt-3" data-testid="pm-terms-checkbox">
                <Form.Check.Input
                  type="checkbox"
                  className="terms-checkbox-input m-0"
                  checked={agreedCheckbox}
                  onChange={(e) => {
                    setAgreedCheckbox(e.target.checked);
                    e.target.checked && setNotAgreedError('')
                  }}
                  data-testid="pm-terms-checkbox-input"
                />
                <label htmlFor="terms-checkbox-input" className="terms-checkbox-label" data-testid="pm-terms-checkbox-label">
                  <FormattedMessage
                    id={CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_TEXT}
                    defaultMessage={CONSTANTS.PAYMENT_CHECKBOX_TEXT}
                  />
                </label>
              </div>
              {!agreedCheckbox &&
                <FormFieldError
                  id="not-agree"
                  value={notAgreedError}
                  userState=""
                  testId="pn-not-agree"
                />
              }
              <PaylaDcs snippetToken={snippetToken} agreed={agreedCheckbox} />
              <div className="term_policy_text mt-3"
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({
                    id: CONSTANTS.PAYMENT_FORM.LABEL.CHECKBOX_DESCRIPTION,
                    defaultMessage: CONSTANTS.PAYMENT_CHECKBOX_DESCRIPTION,
                  }),
                }}
              ></div>
            </Row>

            <Row className="d-flex align-items-center">
              {showPaymentProcessing ? (
                <Col>
                  <PaymentProcessingLoader />
                </Col>
              ) : (
                <Col className="text-end">
                
                  <button
                    className="btn btn-primary text-white px-4 rounded-4 volte-medium"
                    onClick={completePayment}
                    data-testid="pm-submit-btn"
                  >
                    <FormattedMessage id={CONSTANTS.PAYMENT_FORM.BUTTON.SUBMIT_PAYMENT} defaultMessage="complete Payment" />
                  </button>
                </Col>
              )}
            </Row>
          </div>}
        </div>
      }
    </div>
  );
};

export default InstallmentOptions;